import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import ISONumber from "components/formatting/ISONumber";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { QueryField, QueryFieldOption, QueryInfo } from "system/query/QueryInfo";
import { queryOps } from "system/query/Query";
import { AutoTimestampDisplay } from "components/formatting/AutoTimestampDisplay";

export interface QueryBuilderValueBoxProps {
  queryInfo?: QueryInfo;
  selectedField?: QueryField;

  fieldType?: string;
  fieldOptions?: QueryFieldOption[];

  borderColor?: string;
  borderColorTop?: string;

  isViewerMode?: boolean;
  hideTime?: boolean;

  fullWidth?: boolean;
  inline?: boolean;
  op?: queryOps;
  value?: string | number | boolean | null | undefined;
  label?: string | number | boolean | null | undefined;
  children?: ReactNode;

  overrideContent?: boolean;
}

export function QueryBuilderValueBox(props: QueryBuilderValueBoxProps): JSX.Element {
  const theme = useTheme();
  const {i18n} = useTranslation();

  let valueDisplay:JSX.Element = (<Typography variant="h5">{props.value}</Typography>);

  const fieldType = props.fieldType || props.selectedField?.type,
        fieldOptions = props.fieldOptions || props.selectedField?.options;

  if (fieldType === "integer" || fieldType === "decimal") {
    valueDisplay = (<ISONumber typographyVariant="h5" number={props.value as number} />);
  } else if (fieldType === "boolean") {
    if (props.value) {
      valueDisplay = (<Typography variant="h5">{i18n.t("true")}</Typography>);
    } else {
      valueDisplay = (<Typography variant="h5">{i18n.t("false")}</Typography>);
    }
  } else if (props.selectedField?.type === "timestamp") {
    valueDisplay = (<AutoTimestampDisplay value={props.value as string} hideTime={props.hideTime} />);
  } else if (fieldType === "keyword" || fieldType === "keywords" || fieldType === "uid") {
    const options = fieldOptions;
    if (options) {
      const option = options.find((option) => option.id === props.value);
      if (option) {
        valueDisplay = (<Typography variant="h5">{option.label}</Typography>);
      } else if (props.value === null) {
        valueDisplay = (<Typography variant="h5">{i18n.t("null")}</Typography>);
      }
    } else if (props.label) {
      valueDisplay = (<Typography variant="h5">{props.label}</Typography>);
    } else if (props.value === null) {
      valueDisplay = (<Typography variant="h5">{i18n.t("null")}</Typography>);
    }
  }

  const backgroundColor = props.isViewerMode ? "transparent" : theme.header.background,
        paddingStyle = props.isViewerMode ? '1px 2px' : '3px 8px',
        marginStyle = props.isViewerMode ? '1px' : '3px',
        borderTop = props.isViewerMode ? "none" : `1px solid ${props.borderColorTop}`,
        border = props.isViewerMode ? "none" : `1px solid ${props.borderColor}`;

  return (
    <Box
      sx={{m:0, fontWeight:'medium',
        position: 'relative',
        margin: marginStyle,
        padding: paddingStyle,
        display: props.inline ? 'inline-block' : 'block',
        width: props.fullWidth ? '100%' : 'auto',
        opacity: (props.label || props.value !== undefined) ? 1 : 0.6,
        borderRadius:'6px',
        borderTop: border,
        borderLeft: borderTop,
        borderBottom: borderTop,
        borderRight: border,
        backgroundColor: backgroundColor,
        verticalAlign:"middle"
      }}>
      {!props.overrideContent &&
        <>
        {!props.label && props.value === undefined &&
          <Typography variant="h5">
            {i18n.t("query-form.empty-value")}
          </Typography>
        }
        {props.label &&
          <Typography variant="h5">
            {props.label}
          </Typography>
        }
        {!props.label &&
          valueDisplay
        }
        </>
      }
      {props.children}
    </Box>
  );
}
